import { FACEBOOK, INSTAGRAM } from '../config/constants';

export interface BoostConfig {
  active?: boolean;
  goal?: any;
  beneficiary?: string;
  budget?: number;
  days?: number;
  special_requirements?: any;
  interests?: any[];
  countries?: any[];
  cities?: any[];
  gender?: any[];
  min_age?: number;
  max_age?: number;
}

export interface BoostConfigValidationResult {
  isValid: boolean;
  errors: { [platform: string]: string[] };
  hasActiveConfigs: boolean;
}

/**
 * Validates required fields for a specific platform configuration
 */
export const validatePlatformRequiredFields = (
  platformConfig: BoostConfig,
  t: (key: string) => string,
  skipActiveCheck: boolean = false
): string[] => {
  const errors: string[] = [];

  if (!skipActiveCheck && (!platformConfig || !platformConfig.active)) {
    return errors; // Skip validation for inactive platforms
  }

  // Required field validations
  // Ensure beneficiary is not empty or just whitespace
  if (!platformConfig.beneficiary || platformConfig.beneficiary.trim() === '') {
    errors.push(
      t('lomavisCreator.postBoost.validation.beneficiaryRequired') ||
        'Beneficiary is required'
    );
  }

  if (!platformConfig.budget || platformConfig.budget <= 0) {
    errors.push(
      t('lomavisCreator.postBoost.validation.budgetRequired') ||
        'Budget must be greater than 0'
    );
  }

  if (!platformConfig.days || platformConfig.days <= 0) {
    errors.push(
      t('lomavisCreator.postBoost.validation.daysRequired') ||
        'Duration must be greater than 0'
    );
  }

  return errors;
};

/**
 * Validates age fields for a specific platform configuration
 */
export const validatePlatformAgeFields = (
  platformConfig: BoostConfig,
  t: (key: string) => string,
  skipActiveCheck: boolean = false
): string[] => {
  const errors: string[] = [];

  if (!skipActiveCheck && (!platformConfig || !platformConfig.active)) {
    return errors; // Skip validation for inactive platforms
  }

  const { min_age, max_age } = platformConfig;

  if (min_age && (min_age < 13 || min_age > 65)) {
    errors.push(
      t('lomavisCreator.postBoost.validation.minAgeRange') ||
        'Minimum age must be between 13 and 65'
    );
  }

  if (max_age && (max_age < 13 || max_age > 65)) {
    errors.push(
      t('lomavisCreator.postBoost.validation.maxAgeRange') ||
        'Maximum age must be between 13 and 65'
    );
  }

  if (min_age && max_age && min_age >= max_age) {
    errors.push(
      t('lomavisCreator.postBoost.validation.ageRangeInvalid') ||
        'Minimum age must be less than maximum age'
    );
  }

  return errors;
};

/**
 * Validates all boost configurations for enabled platforms
 */
export const validateAllBoostConfigs = (
  boost_config: { [platform: string]: BoostConfig },
  platformEnabled: { [platform: string]: boolean },
  t: (key: string) => string,
  sameBoostForAll: boolean = false
): BoostConfigValidationResult => {
  const errors: { [platform: string]: string[] } = {};
  const boostPlatforms = [FACEBOOK, INSTAGRAM].filter(
    (platform) => platformEnabled[platform]
  );
  let hasActiveConfigs = false;

  boostPlatforms.forEach((platform) => {
    const platformConfig = boost_config[platform];

    // Determine if this platform config should be validated
    const isEffectivelyActive =
      platformConfig &&
      (platformConfig.beneficiary ||
        platformConfig.budget ||
        platformConfig.days ||
        platformConfig.active !== false); // Default to active when null/undefined

    if (isEffectivelyActive) {
      hasActiveConfigs = true;

      // Always skip the active check - validate all platforms with content or active flag
      const skipActiveCheck = true;
      const requiredFieldErrors = validatePlatformRequiredFields(
        platformConfig,
        t,
        skipActiveCheck
      );
      const ageFieldErrors = validatePlatformAgeFields(
        platformConfig,
        t,
        skipActiveCheck
      );
      const allErrors = [...requiredFieldErrors, ...ageFieldErrors];

      if (allErrors.length > 0) {
        errors[platform] = allErrors;
      }
    }
  });

  return {
    isValid: Object.keys(errors).length === 0 && hasActiveConfigs,
    errors,
    hasActiveConfigs
  };
};

/**
 * Checks if boost configuration is valid (for button styling)
 */
export const isValidPostBoostConfig = (
  boost_config: { [platform: string]: BoostConfig },
  platformEnabled: { [platform: string]: boolean },
  t: (key: string) => string,
  sameBoostForAll: boolean = false
): boolean => {
  const validation = validateAllBoostConfigs(
    boost_config,
    platformEnabled,
    t,
    sameBoostForAll
  );
  return validation.isValid;
};
